import { useMap } from "react-map-gl/maplibre";
import { useAircraftPositionsQuery } from "@/api/v0/aircraft";
import { Source, Layer } from "react-map-gl/maplibre";
import { useStore } from "@/store/store";
import { useCallback, useEffect, useState, useMemo } from "react";
import type { AircraftPosition } from "@/api/v0/aircraft";
import type { Feature, Point } from "geojson";

interface Props {
  center: [number, number];
  radius: number;
  zoom: number;
}

// Minimum zoom level to show aircraft
const MIN_ZOOM_LEVEL = 4;
// Minimum zoom level to show labels (callsign + altitude)
const LABEL_MIN_ZOOM_LEVEL = 12;

// Position interpolation settings
const INTERPOLATION_INTERVAL = 500; // Update every 500ms
const MAX_INTERPOLATION_TIME = 10000; // Don't interpolate beyond 10 seconds

// Helper function to interpolate aircraft position
const interpolatePosition = (
  lastPosition: AircraftPosition,
  currentTime: number,
  lastUpdateTime: number
): { lat: number; lon: number; track: number } => {
  const timeSinceUpdate = (currentTime - lastUpdateTime) / 1000; // Convert to seconds
  
  // Don't interpolate if too much time has passed
  if (timeSinceUpdate > MAX_INTERPOLATION_TIME / 1000) {
    return { lat: lastPosition.lat, lon: lastPosition.lon, track: lastPosition.track };
  }
  
  // Convert ground speed from knots to meters per second
  const speedMs = (lastPosition.gs * 0.514444); // 1 knot = 0.514444 m/s
  
  // Calculate distance traveled
  const distanceTraveled = speedMs * timeSinceUpdate;
  
  // Convert distance to degrees (approximate)
  const latOffset = (distanceTraveled / 111000) * Math.cos(lastPosition.track * Math.PI / 180);
  const lonOffset = (distanceTraveled / (111000 * Math.cos(lastPosition.lat * Math.PI / 180))) * Math.sin(lastPosition.track * Math.PI / 180);
  
  return {
    lat: lastPosition.lat + latOffset,
    lon: lastPosition.lon + lonOffset,
    track: lastPosition.track
  };
};

// Create airplane icon with specified fill color - oriented pointing North
const createAirplaneIcon = (fillColor: string) => {
  const svgString = `<?xml version="1.0" encoding="utf-8"?>
    <svg fill="${fillColor}" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
};

export const AircraftLayer = ({ center, radius, zoom }: Props) => {
  const map = useMap();
  const mapStyle = useStore(useCallback((state) => state.mapStyle, []));
  const mapStyleUrl = useStore(useCallback((state) => state.mapStyleUrl, []));
  
  // State for interpolated positions
  const [interpolatedPositions, setInterpolatedPositions] = useState<Map<string, AircraftPosition>>(new Map());
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());

  const { data: aircraftPositions, error, isLoading } = useAircraftPositionsQuery({
    lat: center[1],
    lon: center[0],
    radius,
  });

  // Add/update the aircraft icon to the map style depending on current map style
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap) return;

    // Use yellow color for aircraft icons to make them stand out on the map
    const fillColor = "#eab308"; // yellow-500

    const image = new Image();
    image.onload = () => {
      if (currentMap.hasImage('aircraft-icon')) {
        currentMap.removeImage('aircraft-icon');
      }
      currentMap.addImage('aircraft-icon', image);
    };
    image.src = createAirplaneIcon(fillColor);
  }, [map, mapStyle, mapStyleUrl]);
  
  // Update interpolated positions when new data arrives
  useEffect(() => {
    if (Array.isArray(aircraftPositions)) {
      const currentTime = Date.now();
      const newPositions = new Map<string, AircraftPosition>();

      aircraftPositions.forEach(aircraft => {
        // Calculate interpolated position
        const interpolated = interpolatePosition(aircraft, currentTime, currentTime - (aircraft.seen * 1000));
        
        
        newPositions.set(aircraft.hex, {
          ...aircraft,
          lat: interpolated.lat,
          lon: interpolated.lon,
          track: interpolated.track
        });
      });
      
      setInterpolatedPositions(newPositions);
      setLastUpdateTime(currentTime);
    }
  }, [aircraftPositions]);
  
  // Update interpolated positions every second
  useEffect(() => {
    if (!Array.isArray(aircraftPositions)) return;

    const interval = setInterval(() => {
      const currentTime = Date.now();
      const newPositions = new Map<string, AircraftPosition>();

      aircraftPositions.forEach(aircraft => {
        // Calculate interpolated position based on time since last API update
        const interpolated = interpolatePosition(aircraft, currentTime, lastUpdateTime);
        
        newPositions.set(aircraft.hex, {
          ...aircraft,
          lat: interpolated.lat,
          lon: interpolated.lon,
          track: interpolated.track
        });
      });
      
      setInterpolatedPositions(newPositions);
    }, INTERPOLATION_INTERVAL);
    
    return () => clearInterval(interval);
  }, [aircraftPositions, lastUpdateTime]);
  
  const shouldShowAircraft = useStore((state) => state.shouldShowAircraft);

  // Create GeoJSON features for aircraft using interpolated positions
  const features: Feature<Point>[] = useMemo(() => {
    if (!Array.isArray(aircraftPositions)) return [];

    return aircraftPositions.map((aircraft: AircraftPosition) => {
      const interpolated = interpolatedPositions.get(aircraft.hex) || aircraft;
      
      return {
        type: "Feature",
        properties: {
          hex: aircraft.hex,
          flight: aircraft.flight,
          alt_baro: aircraft.alt_baro,
          gs: aircraft.gs,
          track: interpolated.track,
          category: aircraft.category,
          squawk: aircraft.squawk,
          emergency: aircraft.emergency,
          seen: aircraft.seen,
          seen_pos: aircraft.seen_pos,
        },
        geometry: {
          type: "Point",
          coordinates: [interpolated.lon, interpolated.lat],
        },
      };
    });
  }, [aircraftPositions, interpolatedPositions]);

  // Don't show aircraft if zoomed out too far
  if (zoom < MIN_ZOOM_LEVEL) {
    return null;
  }

  if (!shouldShowAircraft) {
    return null;
  }

  if (error) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (!aircraftPositions?.length) {
    return null;
  }

  return (
    <>
      {/* Aircraft markers */}
      <Source
        type="geojson"
        data={{
          type: "FeatureCollection",
          features,
        }}
      >
        <Layer
          type="symbol"
          layout={{
            "icon-image": "aircraft-icon",
            "icon-size": 1,
            "icon-rotate": ["get", "track"],
            "icon-rotation-alignment": "map",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          }}
        />
      </Source>

      {/* Aircraft labels - only at high zoom */}
      {zoom >= LABEL_MIN_ZOOM_LEVEL && (
        <Source
          type="geojson"
          data={{
            type: "FeatureCollection",
            features,
          }}
        >
          <Layer
            type="symbol"
            layout={{
              "text-field": ["concat", 
                ["coalesce", ["get", "flight"], ["get", "hex"]], 
                " ", 
                ["get", "alt_baro"], 
                "ft"
              ],
              "text-anchor": "top",
              "text-offset": [0, 1.5],
              "text-size": 14,
              "text-allow-overlap": true,
            }}
            paint={{
              "text-color": "#ffffff",
              "text-halo-color": "#000000",
              "text-halo-width": 2,
            }}
          />
        </Source>
      )}
    </>
  );
}; 